/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Session from 'embercom/services/session';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import storage from 'embercom/vendor/intercom/storage';

export enum HandlingEventType {
  Resume = 'resume',
  Pause = 'pause',
}

export enum PauseReason {
  ConversationSwitch = 'conversation_switch',
}

export interface HandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: Date;
  reason?: PauseReason;
}

interface StoredHandlingEvent {
  conversationId: number;
  type: HandlingEventType;
  timestamp: string;
  reason?: PauseReason;
}

const STORAGE_KEY = 'inbox.handling_events';
const ACTIVE_CONVERSATION_KEY = 'inbox.active_handling_conversation';

export default class ConversationHandlingService extends Service {
  @service declare session: Session;

  @tracked private activeConversationId: number | null = null;
  @tracked pendingEvents: HandlingEvent[] = [];

  constructor() {
    super(...arguments);
    this.loadFromStorage();
  }

  private loadFromStorage(): void {
    try {
      // Load pending events
      let eventsJson = storage.get(STORAGE_KEY);
      if (eventsJson) {
        let storedEvents = JSON.parse(eventsJson) as StoredHandlingEvent[];
        this.pendingEvents = storedEvents.map((e) => ({
          conversationId: e.conversationId,
          type: e.type,
          timestamp: new Date(e.timestamp),
          reason: e.reason,
        }));
      }

      // Load active conversation
      let activeConvId = storage.get(ACTIVE_CONVERSATION_KEY);
      if (activeConvId) {
        this.activeConversationId = Number(activeConvId);
        // eslint-disable-next-line no-console
        console.log('[AHT] Restored active conversation', {
          conversationId: this.activeConversationId,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to load from storage', error);
      // Clear corrupted data
      this.clearStorage();
    }
  }

  private saveToStorage(): void {
    try {
      // Save pending events
      let storedEvents: StoredHandlingEvent[] = this.pendingEvents.map((e) => ({
        conversationId: e.conversationId,
        type: e.type,
        timestamp: e.timestamp.toISOString(),
        reason: e.reason,
      }));
      storage.set(STORAGE_KEY, JSON.stringify(storedEvents));

      // Save active conversation
      if (this.activeConversationId) {
        storage.set(ACTIVE_CONVERSATION_KEY, String(this.activeConversationId));
      } else {
        storage.remove(ACTIVE_CONVERSATION_KEY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AHT] Failed to save to storage', error);
    }
  }

  private clearStorage(): void {
    storage.remove(STORAGE_KEY);
    storage.remove(ACTIVE_CONVERSATION_KEY);
    this.pendingEvents = [];
  }

  private shouldTrackConversation(conversation: Conversation): boolean {
    let isOpen = conversation.state === ConversationState.Open;
    let isAssignedToMe = conversation.adminAssignee?.id === this.session.teammate.id;

    return isOpen && isAssignedToMe;
  }

  private createResumeEvent(conversationId: number): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Resume,
      timestamp: new Date(),
    };

    this.pendingEvents.push(event);
    this.activeConversationId = conversationId;
    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Resume event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  private createPauseEvent(conversationId: number, reason?: PauseReason): void {
    let event: HandlingEvent = {
      conversationId,
      type: HandlingEventType.Pause,
      timestamp: new Date(),
      reason,
    };

    this.pendingEvents.push(event);

    if (this.activeConversationId === conversationId) {
      this.activeConversationId = null;
    }

    this.saveToStorage();

    // eslint-disable-next-line no-console
    console.log('[AHT] Pause event created', {
      conversationId,
      timestamp: event.timestamp.toISOString(),
      reason: reason || 'none',
      adminId: this.session.teammate.id,
      pendingCount: this.pendingEvents.length,
    });
  }

  resumeConversation(conversation: Conversation): void {
    // Pause the currently active conversation if switching to a different one
    if (this.activeConversationId && this.activeConversationId !== conversation.id) {
      this.createPauseEvent(this.activeConversationId, PauseReason.ConversationSwitch);
    }

    // Only resume if conversation is trackable (open + assigned to current teammate)
    if (!this.shouldTrackConversation(conversation)) {
      // eslint-disable-next-line no-console
      console.log('[AHT] Conversation not trackable - skipping resume', {
        conversationId: conversation.id,
        state: conversation.state,
        assignedToMe: conversation.adminAssignee?.id === this.session.teammate.id,
      });
      return;
    }

    // Don't create duplicate resume if already active
    if (this.activeConversationId === conversation.id) {
      return;
    }

    this.createResumeEvent(conversation.id);
  }
}

declare module '@ember/service' {
  interface Registry {
    'conversation-handling-service': ConversationHandlingService;
  }
}
